import React, { ReactElement, memo } from 'react';
import ServerErrorAlert from 'adapter/in/dashboards/components/ServerErrorAlert';
import { Row, Cell, Grid } from '@dexma/ui-components';
import i18n from 'asset/translation';
import { StyledFormSubmitModal } from 'adapter/in/common/components/FormSubmitModal/style';
import { ConfirmButtonGroup, ConfirmButtonGroupsDataIdContext } from 'adapter/in/common/components/ConfirmButtonGroup/ConfirmButtonGroup';

export type CreateNameFormData = {
    name: string;
};

export type FormSubmitModalProps = {
    error: boolean;
    success: boolean;
    isLoading: boolean;
    showModal: boolean;
    handleCloseModal: () => void;
    handleOnSubmit: () => void;
    title: string;
    dataIdContext: ConfirmButtonGroupsDataIdContext & { tryAgain: string };
    proceedButtonText?: string;
    children: ReactElement;
    handleOnTryAgain?: () => void;
    handleOnSkip?: () => void;
    disableMainButton?: boolean;
};

const FormSubmitModal = memo(
    (props: FormSubmitModalProps): ReactElement => {
        const {
            showModal,
            handleCloseModal,
            error,
            success,
            isLoading,
            handleOnSubmit,
            title,
            proceedButtonText,
            children,
            handleOnTryAgain,
            dataIdContext,
            handleOnSkip,
            disableMainButton,
        } = props;

        return (
            <StyledFormSubmitModal
                width={600}
                open={showModal}
                onCancel={handleCloseModal}
                title={title}
                footer={
                    <ConfirmButtonGroup
                        isLoading={isLoading}
                        showIcon={success}
                        onMainClick={handleOnSubmit}
                        onCancel={handleCloseModal}
                        mainButtonText={proceedButtonText}
                        mainButtonVariant='primary'
                        onOptionalClick={handleOnSkip}
                        optionalButtonText={handleOnSkip ? i18n.t('common.skip') : undefined}
                        optionalButtonVariant='destructive'
                        proceedType={success ? 'button' : 'submit'}
                        iconBefore={success ? 'check' : undefined}
                        dataIdContext={dataIdContext}
                        disableMainButton={disableMainButton}
                    />
                }
                closeModalButtonAriaLabel={i18n.t('common.ariaLabel.closeModalIcon')}
                body={
                    <form data-testid='form-submit'>
                        <Grid fluid className='no-padding-grid'>
                            <Row>
                                {error && (
                                    <Cell>
                                        <ServerErrorAlert
                                            tryAgain={handleOnTryAgain || handleOnSubmit}
                                            dataId={props.dataIdContext.tryAgain}
                                        />
                                    </Cell>
                                )}
                                {children}
                            </Row>
                        </Grid>
                    </form>
                }
            />
        );
    }
);

export default FormSubmitModal;
